import React, { useEffect, useMemo, useState } from 'react';
import { Lock } from 'lucide-react';
import { useCutierStore } from '../store/useCutierStore';
import {
  fetchLeadsByMonth,
  loadCutierAdminSettings,
  loadCutierInvoiceProfile,
  verifyCutierAdminPin,
  verifyCutierPin,
} from '../services/cutierFirebaseService';
import CutierLeadIngestion from '../components/cutier/CutierLeadIngestion';
import CutierBillingCockpit from '../components/cutier/CutierBillingCockpit';
import CutierLeadTable from '../components/cutier/CutierLeadTable';
import CutierAdminPanel from '../components/cutier/CutierAdminPanel';
import { CUTIER_FIRST_MONTH_KEY } from '../utils/cutier/cutierLeadUtils';
import { CUTIER_LOGO_SRC, RAMIN_DELL_LOGO_SRC } from '../constants/brandAssets';

type CutierInstallPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

const isStandaloneCutier = (): boolean => {
  try {
    return (
      (window.navigator as any).standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches
    );
  } catch {
    return false;
  }
};

/** Ein Klick statt chrome://apps oder Browser-Menüs. */
const CutierInstallButton: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const [installPrompt, setInstallPrompt] = useState<CutierInstallPrompt | null>(
    () => ((window as any).__CUTIER_INSTALL_PROMPT__ as CutierInstallPrompt | null) || null
  );
  const [installed, setInstalled] = useState(isStandaloneCutier);

  useEffect(() => {
    const onReady = () => {
      setInstallPrompt(
        ((window as any).__CUTIER_INSTALL_PROMPT__ as CutierInstallPrompt | null) || null
      );
    };
    const onInstalled = () => {
      setInstallPrompt(null);
      setInstalled(true);
    };
    window.addEventListener('cutierinstallready', onReady);
    window.addEventListener('cutierappinstalled', onInstalled);
    return () => {
      window.removeEventListener('cutierinstallready', onReady);
      window.removeEventListener('cutierappinstalled', onInstalled);
    };
  }, []);

  if (installed) return null;

  const install = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === 'accepted') {
      setInstallPrompt(null);
      setInstalled(true);
    }
  };

  if (!installPrompt) {
    return compact ? null : (
      <p className="rounded-xl border border-pe-border-strong bg-pe-input px-4 py-3 text-center text-xs text-pe-muted">
        Falls bereits „In App öffnen“ erscheint, ist die neue Cutier-App schon installiert.
      </p>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void install()}
      className={`rounded-xl bg-emerald-500 font-black uppercase tracking-widest text-slate-950 shadow-lg hover:bg-emerald-400 ${
        compact ? 'px-4 py-2 text-[10px]' : 'w-full px-4 py-4 text-xs'
      }`}
    >
      Cutier als App installieren
    </button>
  );
};

const ThemeToggle: React.FC = () => {
  const [dark, setDark] = React.useState(() => document.documentElement.classList.contains('dark'));
  const toggle = () => {
    const next = !dark;
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('pe-theme', next ? 'dark' : 'light');
    setDark(next);
  };
  return (
    <button type="button" onClick={toggle} title={dark ? 'Light Mode' : 'Dark Mode'}
      className="rounded-xl border border-pe-border-strong bg-pe-card p-2 text-pe-muted hover:text-pe-accent-text hover:border-pe-accent/50 transition-colors"
    >
      {dark ? (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
      )}
    </button>
  );
};

const PartnerLogos: React.FC = () => (
  <div className="flex flex-wrap items-center justify-center gap-4">
    <div className="rounded-2xl border border-[#E6C299]/40 bg-[#111111] px-5 py-4">
      <img
        src={CUTIER_LOGO_SRC}
        alt="CUTIER"
        className="h-24 w-auto max-w-[280px] object-contain"
      />
    </div>
    <div className="rounded-2xl border border-[#E6C299]/40 bg-[#111111] px-5 py-4">
      <img
        src={RAMIN_DELL_LOGO_SRC}
        alt="ramin dell luxury hair"
        className="h-24 w-auto max-w-[280px] object-contain"
      />
    </div>
  </div>
);

const buildMonthOptions = (): string[] => {
  const now = new Date();
  const keys: string[] = [];
  for (let i = 0; i < 24; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (key < CUTIER_FIRST_MONTH_KEY) break;
    keys.push(key);
  }
  if (keys.length === 0) keys.push(CUTIER_FIRST_MONTH_KEY);
  return keys;
};

const formatMonthLabel = (monthKey: string): string => {
  const [y, m] = monthKey.split('-');
  const date = new Date(Number(y), Number(m) - 1, 1);
  if (Number.isNaN(date.getTime())) return monthKey;
  return date.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
};

const CutierHubPage: React.FC = () => {
  const isPartnerAuthenticated = useCutierStore((s) => s.isPartnerAuthenticated);
  const selectedMonth = useCutierStore((s) => s.selectedMonth);
  const setPartnerAuthenticated = useCutierStore((s) => s.setPartnerAuthenticated);
  const setSelectedMonth = useCutierStore((s) => s.setSelectedMonth);
  const setLeads = useCutierStore((s) => s.setLeads);
  const setLoading = useCutierStore((s) => s.setLoading);
  const setError = useCutierStore((s) => s.setError);

  const setAdminSettings = useCutierStore((s) => s.setAdminSettings);
  const setInvoiceProfile = useCutierStore((s) => s.setInvoiceProfile);
  const setInvoiceProfileLoaded = useCutierStore((s) => s.setInvoiceProfileLoaded);

  const [sessionReady, setSessionReady] = useState(() => useCutierStore.persist.hasHydrated());
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinBusy, setPinBusy] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [adminPin, setAdminPin] = useState('');
  const [adminPinError, setAdminPinError] = useState('');
  const monthOptions = useMemo(() => buildMonthOptions(), []);

  useEffect(() => {
    if (useCutierStore.persist.hasHydrated()) {
      setSessionReady(true);
      return;
    }
    return useCutierStore.persist.onFinishHydration(() => setSessionReady(true));
  }, []);

  useEffect(() => {
    if (selectedMonth < CUTIER_FIRST_MONTH_KEY) {
      setSelectedMonth(CUTIER_FIRST_MONTH_KEY);
    }
  }, [selectedMonth, setSelectedMonth]);

  useEffect(() => {
    if (!isPartnerAuthenticated) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [rows, settings, invoiceProfile] = await Promise.all([
          fetchLeadsByMonth(selectedMonth),
          loadCutierAdminSettings(),
          loadCutierInvoiceProfile(),
        ]);
        if (!cancelled) {
          setLeads(rows);
          setAdminSettings(settings);
          setInvoiceProfile(invoiceProfile);
          setInvoiceProfileLoaded(true);
        }
      } catch {
        if (!cancelled) setError('Leads konnten nicht geladen werden.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [isPartnerAuthenticated, selectedMonth, setAdminSettings, setError, setInvoiceProfile, setInvoiceProfileLoaded, setLeads, setLoading]);

  const handleAdminUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdminPinError('');
    const ok = await verifyCutierAdminPin(adminPin);
    if (ok) {
      setAdminUnlocked(true);
      setAdminPin('');
    } else {
      setAdminPinError('Admin-Passwort ungültig.');
    }
  };

  const handleLogout = () => {
    setAdminOpen(false);
    setAdminUnlocked(false);
    setAdminPin('');
    setPartnerAuthenticated(false);
  };

  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPinError('');
    setPinBusy(true);
    try {
      const ok = await verifyCutierPin(pin);
      if (ok) {
        setPartnerAuthenticated(true);
        setPin('');
      } else {
        setPinError('PIN ungültig.');
      }
    } catch {
      setPinError('Prüfung fehlgeschlagen.');
    } finally {
      setPinBusy(false);
    }
  };

  if (!sessionReady) {
    return <div className="min-h-screen bg-pe-bg" aria-busy="true" />;
  }

  if (!isPartnerAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-pe-bg p-4">
        <div className="w-full max-w-md rounded-2xl border border-pe-border-strong bg-pe-card p-8 text-pe-text shadow-2xl">
          <PartnerLogos />
          <h1 className="mt-6 text-center text-lg font-black uppercase tracking-wider">
            CUTIER & Ramin Dell Partner-Hub
          </h1>
          <p className="mt-2 text-center text-xs text-pe-muted">
            Bitte Partner-PIN eingeben
          </p>
          <div className="mt-6">
            <CutierInstallButton />
          </div>
          <form onSubmit={handlePinSubmit} className="mt-6 space-y-4">
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="PIN"
              autoComplete="off"
              className="w-full rounded-xl border border-pe-border-strong bg-pe-input px-4 py-3 text-sm text-pe-text focus:border-[#E6C299] focus:outline-none"
            />
            {pinError ? <p className="text-xs text-red-400">{pinError}</p> : null}
            <button
              type="submit"
              disabled={pinBusy || !pin.trim()}
              className="w-full rounded-xl bg-[#E6C299] px-4 py-3 text-xs font-black uppercase tracking-widest text-[#030303] hover:bg-[#d4b086] disabled:opacity-50"
            >
              {pinBusy ? 'Prüfe…' : 'Zugang'}
            </button>
          </form>
          <p className="mt-4 text-center text-[11px] leading-relaxed text-pe-muted">
            Einmal installieren und anmelden – danach öffnet Cutier direkt und die Anmeldung
            bleibt auf diesem Gerät erhalten.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-pe-bg p-4 text-pe-text md:p-8">
      <header className="mx-auto mb-8 flex max-w-6xl flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <PartnerLogos />
          <p className="mt-3 text-center text-[10px] font-bold uppercase tracking-widest text-pe-accent-text md:text-left">
            Seminar-Teilnehmererfassung & Abrechnung
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3 md:justify-end">
          <CutierInstallButton compact />
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="rounded-xl border border-pe-border-strong bg-pe-card px-3 py-2 text-sm text-pe-text focus:border-[#E6C299] focus:outline-none"
          >
            {monthOptions.map((key) => (
              <option key={key} value={key}>
                {formatMonthLabel(key)}
              </option>
            ))}
          </select>
          <ThemeToggle />
          <button
            type="button"
            onClick={() => setAdminOpen((open) => !open)}
            className={`rounded-xl border p-2 ${
              adminOpen
                ? 'border-[#E6C299]/50 bg-[#E6C299]/10 text-pe-accent-text'
                : 'border-pe-border-strong bg-pe-card text-pe-muted hover:text-pe-text'
            }`}
            aria-label="Admin"
            title="Admin"
          >
            <Lock className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="rounded-xl border border-pe-border-strong bg-pe-card px-4 py-2 text-[10px] font-black uppercase tracking-widest text-pe-muted hover:text-pe-text"
          >
            Abmelden
          </button>
        </div>
      </header>

      {adminOpen ? (
        <div className="mx-auto mb-6 max-w-6xl">
          {adminUnlocked ? (
            <CutierAdminPanel />
          ) : (
            <form
              onSubmit={handleAdminUnlock}
              className="flex flex-wrap items-end gap-3 rounded-2xl border border-[#E6C299]/40 bg-pe-card p-4"
            >
              <label className="min-w-[200px] flex-1">
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-pe-muted">
                  Admin-Passwort
                </span>
                <input
                  type="password"
                  value={adminPin}
                  onChange={(e) => setAdminPin(e.target.value)}
                  autoComplete="off"
                  className="w-full rounded-xl border border-pe-border-strong bg-pe-input px-3 py-2 text-sm text-pe-text focus:border-[#E6C299] focus:outline-none"
                />
              </label>
              <button
                type="submit"
                disabled={!adminPin.trim()}
                className="rounded-xl bg-[#E6C299] px-4 py-2 text-[10px] font-black uppercase tracking-widest text-[#030303] disabled:opacity-50"
              >
                Öffnen
              </button>
              {adminPinError ? <p className="w-full text-xs text-red-400">{adminPinError}</p> : null}
            </form>
          )}
        </div>
      ) : null}

      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 md:grid-cols-2">
        <section className="min-h-[280px] rounded-2xl border border-pe-border-strong bg-pe-card p-6">
          <h2 className="text-sm font-black uppercase tracking-wider text-pe-accent-text">
            Teilnehmer-Erfassung (CUTIER)
          </h2>
          <div className="mt-4">
            <CutierLeadIngestion />
          </div>
        </section>
        <section className="min-h-[280px] rounded-2xl border border-pe-border-strong bg-pe-card p-6">
          <h2 className="text-sm font-black uppercase tracking-wider text-pe-accent-text">
            Abrechnungs-Cockpit (Ramin Dell)
          </h2>
          <div className="mt-4">
            <CutierBillingCockpit />
          </div>
        </section>
        <section className="rounded-2xl border border-pe-border-strong bg-pe-card p-6 md:col-span-2">
          <CutierLeadTable />
        </section>
      </div>
    </div>
  );
};

export default CutierHubPage;
